import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { sermonsApi } from "../lib/sermonsApi";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../components/ui/select";
import {
  Sparkles,
  FileText,
  Trash2,
  Copy,
  Download,
  ChevronLeft,
  Loader2,
  Save,
  History,
  Plus,
  Search,
  ShieldAlert,
} from "lucide-react";
import { useAuth } from "../lib/authContext";

const TIPOS = [
  { v: "expositivo", l: "Expositivo" },
  { v: "tematico", l: "Temático" },
  { v: "textual", l: "Textual" },
  { v: "narrativo", l: "Narrativo (expositivo de história)" },
  { v: "evangelistico", l: "Evangelístico" },
  { v: "doutrinario", l: "Doutrinário" },
];
const PROFUNDIDADES = [
  { v: "basica", l: "Básica (novos convertidos)" },
  { v: "intermediaria", l: "Intermediária (membros)" },
  { v: "avancada", l: "Avançada (líderes/seminários)" },
];

const DEFAULT_FORM = {
  tema: "",
  texto_base: "",
  tipo_sermao: "expositivo",
  publico_alvo: "Congregação adulta mista",
  duracao: 30,
  profundidade: "intermediaria",
  tom: "Pastoral acolhedor",
  observacoes: "",
};

function FieldLabel({ children }) {
  return (
    <span className="text-[10px] uppercase tracking-[0.25em] text-navy/60 mb-1.5 block">
      {children}
    </span>
  );
}

function GenerateForm({ onGenerate, loading }) {
  const [form, setForm] = useState(DEFAULT_FORM);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setEvt = (k) => (e) => set(k)(e.target.value);

  const submit = (e) => {
    e.preventDefault();
    if (form.tema.trim().length < 3) {
      toast.error("Informe um tema com pelo menos 3 caracteres.");
      return;
    }
    onGenerate({
      ...form,
      duracao: Math.max(5, Math.min(120, parseInt(form.duracao || 30, 10))),
    });
  };

  return (
    <form
      onSubmit={submit}
      data-testid="sermons-generate-form"
      className="space-y-4 bg-white p-6 rounded-2xl border border-border"
    >
      <div>
        <FieldLabel>Tema central *</FieldLabel>
        <Input
          required
          maxLength={240}
          placeholder="Ex.: A graça é suficiente em nossas fraquezas"
          value={form.tema}
          onChange={setEvt("tema")}
          data-testid="sermon-input-tema"
          className="h-11 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
        />
      </div>

      <div>
        <FieldLabel>Texto-base sugerido</FieldLabel>
        <Input
          maxLength={400}
          placeholder='Ex.: 2 Coríntios 12:7-10 (deixe em branco para a IA sugerir)'
          value={form.texto_base}
          onChange={setEvt("texto_base")}
          data-testid="sermon-input-texto-base"
          className="h-11 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <FieldLabel>Tipo de sermão</FieldLabel>
          <Select value={form.tipo_sermao} onValueChange={set("tipo_sermao")}>
            <SelectTrigger
              data-testid="sermon-select-tipo"
              className="h-11 bg-[hsl(var(--parchment))] border-transparent"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIPOS.map((t) => (
                <SelectItem key={t.v} value={t.v}>
                  {t.l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <FieldLabel>Profundidade teológica</FieldLabel>
          <Select
            value={form.profundidade}
            onValueChange={set("profundidade")}
          >
            <SelectTrigger
              data-testid="sermon-select-profundidade"
              className="h-11 bg-[hsl(var(--parchment))] border-transparent"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PROFUNDIDADES.map((p) => (
                <SelectItem key={p.v} value={p.v}>
                  {p.l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2">
          <FieldLabel>Público-alvo</FieldLabel>
          <Input
            maxLength={120}
            value={form.publico_alvo}
            onChange={setEvt("publico_alvo")}
            data-testid="sermon-input-publico"
            className="h-11 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
          />
        </div>
        <div>
          <FieldLabel>Duração (min)</FieldLabel>
          <Input
            type="number"
            min={5}
            max={120}
            value={form.duracao}
            onChange={setEvt("duracao")}
            data-testid="sermon-input-duracao"
            className="h-11 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
          />
        </div>
      </div>

      <div>
        <FieldLabel>Tom desejado</FieldLabel>
        <Input
          maxLength={120}
          value={form.tom}
          onChange={setEvt("tom")}
          data-testid="sermon-input-tom"
          className="h-11 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
        />
      </div>

      <div>
        <FieldLabel>Observações adicionais (opcional)</FieldLabel>
        <Textarea
          rows={3}
          maxLength={1000}
          placeholder="Ênfases doutrinárias, ilustrações desejadas, contexto da igreja…"
          value={form.observacoes}
          onChange={setEvt("observacoes")}
          data-testid="sermon-input-observacoes"
          className="bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
        />
      </div>

      <Button
        type="submit"
        disabled={loading}
        data-testid="sermon-generate-submit"
        className="rounded-full bg-navy text-white hover:bg-gold hover:text-navy uppercase tracking-widest text-xs w-full sm:w-auto"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Gerando sermão…
          </>
        ) : (
          <>
            <Sparkles className="w-4 h-4 mr-2" /> Gerar sermão com IA
          </>
        )}
      </Button>
      {loading && (
        <p className="text-xs text-navy/60 leading-relaxed">
          A IA pode levar de 30 a 90 segundos para entregar um sermão completo
          (11 seções). Não feche esta aba.
        </p>
      )}
    </form>
  );
}

function HistoryList({ items, onOpen, onDelete, loading }) {
  const [search, setSearch] = useState("");
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (s) =>
        (s.titulo || "").toLowerCase().includes(q) ||
        (s.tema || "").toLowerCase().includes(q),
    );
  }, [items, search]);

  return (
    <div className="space-y-3" data-testid="sermons-history">
      <div className="flex items-center justify-between">
        <h4 className="font-display text-lg text-navy flex items-center gap-2">
          <History className="w-4 h-4 text-gold" /> Histórico ({items.length})
        </h4>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
          <Input
            placeholder="Buscar…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 pl-8 text-sm bg-[hsl(var(--parchment))] border-transparent w-44"
            data-testid="sermon-search"
          />
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-navy/55">Carregando…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-navy/55 italic">
          Nenhum sermão {search ? "encontrado para essa busca" : "salvo ainda"}.
        </p>
      ) : (
        <ul className="space-y-2">
          {filtered.map((s) => (
            <li
              key={s.id}
              className="bg-white border border-border rounded-xl p-3 flex items-start justify-between gap-3 hover:border-gold/60 transition-colors"
              data-testid={`sermon-history-item-${s.id}`}
            >
              <button
                onClick={() => onOpen(s.id)}
                className="text-left flex-1 min-w-0"
              >
                <p className="text-[10px] uppercase tracking-widest text-gold">
                  {s.status === "final" ? "Final" : "Rascunho"} ·{" "}
                  {(s.duracao || 30) + " min"} ·{" "}
                  {new Date(s.updated_at).toLocaleDateString("pt-BR")}
                </p>
                <p className="font-display text-navy text-base mt-0.5 truncate">
                  {s.titulo || "Sem título"}
                </p>
                <p className="text-xs text-navy/60 truncate">{s.tema}</p>
              </button>
              <button
                onClick={() => {
                  if (!window.confirm("Excluir este sermão?")) return;
                  onDelete(s.id);
                }}
                className="p-1.5 rounded-lg hover:bg-rose-50"
                data-testid={`sermon-delete-${s.id}`}
                aria-label="Excluir"
              >
                <Trash2 className="w-4 h-4 text-destructive" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SermonViewer({ sermon, onBack, onUpdate, onDelete }) {
  const [titulo, setTitulo] = useState(sermon.titulo || "");
  const [conteudo, setConteudo] = useState(sermon.conteudo_md || "");
  const [saving, setSaving] = useState(false);
  const dirty =
    titulo !== (sermon.titulo || "") ||
    conteudo !== (sermon.conteudo_md || "");

  const save = async (newStatus) => {
    setSaving(true);
    try {
      const payload = { titulo, conteudo_md: conteudo };
      if (newStatus) payload.status = newStatus;
      await onUpdate(sermon.id, payload);
      toast.success(
        newStatus === "final" ? "Marcado como Final." : "Sermão salvo.",
      );
    } catch (e) {
      toast.error("Erro ao salvar. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(conteudo);
      toast.success("Sermão copiado para a área de transferência.");
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  const exportPdf = () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 56;
    const usableW = pageW - margin * 2;
    let y = margin;

    const writeLine = (text, fontSize, weight = "normal") => {
      doc.setFont("times", weight);
      doc.setFontSize(fontSize);
      const wrapped = doc.splitTextToSize(text, usableW);
      wrapped.forEach((ln) => {
        if (y > pageH - margin) {
          doc.addPage();
          y = margin;
        }
        doc.text(ln, margin, y);
        y += fontSize * 1.35;
      });
    };

    // Header
    doc.setFont("times", "bold");
    doc.setFontSize(16);
    doc.text(titulo || "Sermão", margin, y);
    y += 22;
    doc.setFont("times", "italic");
    doc.setFontSize(11);
    doc.setTextColor(120);
    doc.text(
      `Tema: ${sermon.tema || ""}    Duração: ${sermon.duracao || 30} min`,
      margin,
      y,
    );
    y += 22;
    doc.setTextColor(0);

    // Body: parse markdown headings ## as bold larger
    const blocks = conteudo.split(/\n{2,}/);
    blocks.forEach((b) => {
      const t = b.trim();
      if (!t) return;
      if (t.startsWith("## ")) {
        y += 6;
        writeLine(t.replace(/^##\s*/, ""), 13, "bold");
        y += 2;
      } else if (t.startsWith("# ")) {
        y += 8;
        writeLine(t.replace(/^#\s*/, ""), 15, "bold");
        y += 2;
      } else {
        writeLine(t.replace(/\*\*/g, ""), 11, "normal");
        y += 4;
      }
    });

    // Footer
    const total = doc.internal.getNumberOfPages();
    for (let i = 1; i <= total; i++) {
      doc.setPage(i);
      doc.setFont("times", "italic");
      doc.setFontSize(9);
      doc.setTextColor(140);
      doc.text(
        `Sermão gerado via Plataforma Pastoral C E Medeiros, pág. ${i}/${total}`,
        margin,
        pageH - 24,
      );
    }
    const slug = (titulo || "sermao")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60);
    doc.save(`${slug || "sermao"}.pdf`);
  };

  return (
    <div className="space-y-4" data-testid="sermon-viewer">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-navy/70 hover:text-gold"
          data-testid="sermon-back"
        >
          <ChevronLeft className="w-4 h-4" /> Voltar
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={copyToClipboard}
            data-testid="sermon-copy"
            className="rounded-full text-xs"
          >
            <Copy className="w-3.5 h-3.5 mr-1.5" /> Copiar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportPdf}
            data-testid="sermon-export-pdf"
            className="rounded-full text-xs"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" /> Baixar PDF
          </Button>
          <Button
            size="sm"
            onClick={() => save()}
            disabled={!dirty || saving}
            data-testid="sermon-save-draft"
            className="rounded-full text-xs bg-navy text-white hover:bg-gold hover:text-navy"
          >
            <Save className="w-3.5 h-3.5 mr-1.5" />
            {saving ? "Salvando…" : "Salvar rascunho"}
          </Button>
          <Button
            size="sm"
            onClick={() => save("final")}
            disabled={saving}
            data-testid="sermon-save-final"
            className="rounded-full text-xs bg-gold text-navy hover:bg-navy hover:text-white"
          >
            Marcar como Final
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              if (!window.confirm("Excluir este sermão?")) return;
              onDelete(sermon.id);
            }}
            data-testid="sermon-delete-viewer"
            className="rounded-full text-xs text-destructive border-destructive/30 hover:bg-rose-50"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Excluir
          </Button>
        </div>
      </div>

      <div className="bg-white border border-border rounded-2xl p-6">
        <FieldLabel>Título</FieldLabel>
        <Input
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          maxLength={180}
          data-testid="sermon-title-input"
          className="h-11 bg-[hsl(var(--parchment))] border-transparent focus:bg-white font-display text-lg"
        />
        <div className="mt-4 grid sm:grid-cols-3 gap-3 text-xs text-navy/70">
          <div>
            <FieldLabel>Tema</FieldLabel>
            <p className="text-navy">{sermon.tema || ", "}</p>
          </div>
          <div>
            <FieldLabel>Texto base</FieldLabel>
            <p className="text-navy">{sermon.texto_base || ", "}</p>
          </div>
          <div>
            <FieldLabel>Duração</FieldLabel>
            <p className="text-navy">{sermon.duracao || 30} minutos</p>
          </div>
        </div>
        <div className="mt-4">
          <FieldLabel>Conteúdo do sermão (Markdown editável)</FieldLabel>
          <Textarea
            rows={26}
            value={conteudo}
            onChange={(e) => setConteudo(e.target.value)}
            data-testid="sermon-content-textarea"
            className="font-mono text-sm bg-[hsl(var(--parchment))] border-transparent focus:bg-white leading-relaxed"
          />
        </div>
      </div>
    </div>
  );
}

export default function AdminSermonsModule() {
  const { isAdmin } = useAuth();
  const [view, setView] = useState("compose"); // 'compose' | 'viewer'
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingGen, setLoadingGen] = useState(false);
  const [currentSermon, setCurrentSermon] = useState(null);

  const reload = async () => {
    setLoadingList(true);
    try {
      const r = await sermonsApi.list();
      setItems(r);
    } catch {
      toast.error("Não foi possível carregar o histórico.");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (isAdmin) reload();
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <Card className="p-6 border-border bg-white" data-testid="sermons-forbidden">
        <ShieldAlert className="w-5 h-5 text-gold" />
        <p className="font-display text-xl text-navy mt-2">Acesso restrito</p>
        <p className="text-sm text-navy/65 mt-2">
          O módulo IA Sermões é restrito a administradores.
        </p>
      </Card>
    );
  }

  const onGenerate = async (payload) => {
    setLoadingGen(true);
    try {
      const created = await sermonsApi.generate(payload);
      toast.success("Sermão gerado e salvo como rascunho.");
      setItems((prev) => [created, ...prev.filter((p) => p.id !== created.id)]);
      setCurrentSermon(created);
      setView("viewer");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Falha ao gerar o sermão.";
      toast.error(typeof msg === "string" ? msg : "Falha ao gerar o sermão.");
    } finally {
      setLoadingGen(false);
    }
  };

  const onOpen = async (id) => {
    try {
      const s = await sermonsApi.get(id);
      setCurrentSermon(s);
      setView("viewer");
    } catch {
      toast.error("Sermão não encontrado.");
    }
  };

  const onUpdate = async (id, payload) => {
    const updated = await sermonsApi.update(id, payload);
    setCurrentSermon(updated);
    setItems((prev) => prev.map((p) => (p.id === id ? updated : p)));
    return updated;
  };

  const onDelete = async (id) => {
    try {
      await sermonsApi.remove(id);
      setItems((prev) => prev.filter((p) => p.id !== id));
      if (currentSermon?.id === id) {
        setCurrentSermon(null);
        setView("compose");
      }
      toast.success("Sermão removido.");
    } catch {
      toast.error("Não foi possível remover.");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-sermons-module">
      <div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
          IA Ministerial
        </p>
        <h2 className="font-display text-3xl text-navy mt-1">
          Faça seu Sermão
        </h2>
        <p className="text-sm text-navy/70 mt-2 max-w-2xl leading-relaxed">
          A IA produz uma estrutura completa de 11 seções (título, tema, texto
          base, contexto, exegese, pontos, ilustrações, aplicação, apelo e
          oração) seguindo a tradição evangélico-reformada e a versão Almeida.
          Edite livremente e exporte em PDF.
        </p>
      </div>

      {view === "viewer" && currentSermon ? (
        <SermonViewer
          sermon={currentSermon}
          onBack={() => {
            setView("compose");
            setCurrentSermon(null);
          }}
          onUpdate={onUpdate}
          onDelete={onDelete}
        />
      ) : (
        <div className="grid lg:grid-cols-[1fr_360px] gap-6">
          <GenerateForm onGenerate={onGenerate} loading={loadingGen} />
          <Card className="p-5 border-border bg-[hsl(var(--parchment))]">
            <HistoryList
              items={items}
              loading={loadingList}
              onOpen={onOpen}
              onDelete={onDelete}
            />
          </Card>
        </div>
      )}
    </div>
  );
}
